import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Button,
  LogOutButton,
  Progress,
  QuestionnaireSidePanelForm,
  AdminApproveRejectButtons
} from '@/components';
import { BREAKPOINTS, Col, Container, Flex } from '@/ui/layout';
import { Body2Light, Body4, Text, Title } from '@/ui/typography';
import { ExternalLink, TextTooltip } from '@/ui/atoms';
import { EntitySection } from '@/modules/shared/components/EntitySection';
import styled from 'styled-components';
import { SHOWED_SECTIONS_COUNT, STATUSES_WITH_DISABLED } from '@/utils/static/kyc';
import { useDispatch } from 'react-redux';
import { entitiesActions } from '@/store/entities';
import { Divider, FloatButton, Form, Typography, notification, Empty } from 'antd';
import { ArrowLeftOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Scoring } from '@/modules/Questionnaire/components/Scoring';
import { COLORS } from '@/utils/static';
import { useEntityStatus } from '@/hooks';

export const QuestionnaireView = ({
  questionnaire,
  filteredQuestionnaire,
  currentSection,
  currentStep,
  onNext,
  onBack,
  onFinish,
  adminView = false,
  adminEditView = false,
  referralView = false,
  rejectQuestionnaire,
  acceptQuestionnaire,
  disableAccept,
  rejectedQuestions,
  setCurrentSection,
  disabled = false,
  stepsCount = 0,
  statusSettings,
  disabledNext = false,
  control,
  handleSubmit,
  watch,
  setValue,
  trigger,
  errors,
  submitCount,
  preFilledQuestions,
  creators,
  scoring,
  isReadTooltip = true,
  verificationPhotos,
  handleVerificationPhoto,
  isVeriffQuestionnaire,
  sumSubStatusRes,
  refetchSumSubRes,
  ...props
}) => {
  const [api, contextHolder] = notification.useNotification();

  const { t } = useTranslation();
  const { id: investorId } = useParams();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const toggleSidePanel = () => setIsSidePanelOpen(!isSidePanelOpen);
  const [activeRiskNotification, setActiveRiskNotification] = useState(null);

  useEffect(() => {
    if (Object.keys(errors).length > 0)
      dispatch(
        entitiesActions.addToastInfo(
          t('Some fields are not valid. Please check the questions for any error texts.')
        )
      );
  }, [submitCount]);

  const { isRejected, isQuestionRejected, isInProcess, isInReview } =
    useEntityStatus(questionnaire);

  const backToProfile = () => {
    adminView || adminEditView
      ? navigate(`/investor/${investorId}/questionnaires`)
      : navigate('/profile/questionnaires');
  };

  const startSlicePos = currentSection * SHOWED_SECTIONS_COUNT;

  useEffect(() => {
    if (!scoring || !scoring.categoryScoring) return;
    if (!scoring.categoryScoring.comments || scoring.categoryScoring.comments.length === 0) {
      api.destroy();
      setActiveRiskNotification(null);
    }
    if (
      scoring.categoryScoring.comments &&
      scoring.categoryScoring.comments.length > 0 &&
      activeRiskNotification !== scoring.categoryScoring.id
    ) {
      setActiveRiskNotification(scoring.categoryScoring.id);
      api.open({
        message: t('Comments'),
        placement: 'bottomLeft',
        closeIcon: null,
        description: (
          <>
            {scoring.categoryScoring.comments.map((comment) => (
              <Typography.Paragraph>{comment.comment}</Typography.Paragraph>
            ))}
          </>
        ),
        duration: 0,
        icon: <ExclamationCircleOutlined style={{ color: '#d10000' }} />
      });
    }
  }, [scoring]);

  return (
    <>
      {contextHolder}

      <HeaderRow justify='space-between'>
        <Container $mdHidden>
          <Container $maxWidth={238} $wide>
            <Button
              type='text'
              size='large'
              text={t('Back to questionnaires')}
              icon={<ArrowLeftOutlined />}
              onClick={backToProfile}
            />
          </Container>
        </Container>

        <Container $mdVisible $hidden>
          <Button type='text' size='large' icon={<ArrowLeftOutlined />} onClick={backToProfile} />
        </Container>

        <LogOutButton />
      </HeaderRow>

      <StyledRow justify={'center'}>
        <Col>
          <Container
            $paddingLeft={window.innerWidth <= BREAKPOINTS.md ? 'unset' : 16}
            $minWidth={window.innerWidth <= BREAKPOINTS.md ? 'unset' : 200}
            $maxWidth={window.innerWidth <= BREAKPOINTS.md ? 'unset' : 350}
          >
            <CustomFloatButton
              onClick={toggleSidePanel}
              icon={<MenuUnfoldOutlined />}
              style={{ left: '32px', bottom: '24px' }}
            />

            <QuestionnaireSidePanelForm
              isOpen={isSidePanelOpen}
              onClose={toggleSidePanel}
              steps={questionnaire.questionSections}
              currentStep={currentStep}
              step={currentSection + 1}
              setStep={
                referralView ||
                adminView ||
                adminEditView ||
                isRejected ||
                isQuestionRejected ||
                isInReview ||
                isInProcess
                  ? setCurrentSection
                  : null
              }
              isAdmin={adminView}
              isReferralView={referralView}
              isInProcess={isInProcess}
              isInReview={isInReview}
              control={control}
            />
          </Container>
        </Col>

        <CustomDivider type='vertical' style={{ height: 'unset' }} />

        <Col $wide $height='100%'>
          <Flex justify='flex-end' align='center' gap={8} $marginTop={-25} $marginBottom={25}>
            <AdminApproveRejectButtons
              adminView={adminView}
              statusSettings={statusSettings}
              onReject={rejectQuestionnaire}
              onApprove={acceptQuestionnaire}
              rejectDisabled={disableAccept}
              variant='inline'
            />
          </Flex>

          <Flex vertical align='center' $paddingLeft={16} $paddingRight={16} $height='100%'>
            <Flex vertical align='center' $wide>
              {!disabled &&
                questionnaire.questionSections &&
                Array.from(questionnaire.questionSections).length > 0 &&
                questionnaire.name && <Title level={2}>{t(`${questionnaire.name}`)}</Title>}
              {questionnaire.questionSections &&
                Array.from(questionnaire.questionSections).length > 0 && (
                  <Flex $marginTop={40} vertical gap={15} align='center'>
                    {questionnaire.questionSections[currentSection].tooltips && (
                      <TextTooltip
                        isCTA={true}
                        isVeriffQuestionnaire={
                          filteredQuestionnaire?.[0]?.visibleTypeGroup === 'URL'
                        }
                        isReadTooltip={isReadTooltip}
                        title='Document requirements'
                        questionSectionId={questionnaire.questionSections[currentSection].id}
                        textArray={
                          questionnaire.questionSections[currentSection].tooltips &&
                          questionnaire.questionSections[currentSection].tooltips.length > 0
                            ? questionnaire.questionSections[currentSection].tooltips[0].tooltips
                            : null
                        }
                      />
                    )}
                    <Text $fontSize={20} $fontWeight={600}>
                      {t(`${questionnaire.questionSections[currentSection].section}`)}
                    </Text>
                  </Flex>
                )}
              <Container $wide $maxWidth={600} $marginTop={14}>
                <Progress
                  steps={questionnaire.questionSections
                    .sort((a, b) =>
                      a.newNumber
                        ? a.newNumber - b.newNumber
                        : a.number
                          ? a.number - b.newNumber
                          : 0
                    )
                    .map((item) => item.section)}
                  currentStep={currentSection + 1}
                />
              </Container>
              {isVeriffQuestionnaire && sumSubStatusRes?.data?.myStatus === 'Accepted' && (
                <Flex vertical style={{ width: '100%', maxWidth: 600 }}>
                  <Divider />
                  <Text style={{ fontWeight: 'bold' }}>
                    {t("Verification is completed. Click the 'Back to questionnaires' button.")}
                  </Text>
                  <Divider style={{ marginBottom: 0 }} />
                </Flex>
              )}
            </Flex>

            {(!filteredQuestionnaire || filteredQuestionnaire.length === 0) && (
              <Col>
                <Empty
                  description={t(
                    'Something went wrong. Please, reload the page or try again later.'
                  )}
                />
              </Col>
            )}

            {filteredQuestionnaire && filteredQuestionnaire.length > 0 && (
              <>
                <Flex justify='flex-start' vertical $wide style={{ maxWidth: '600px' }}>
                  <Container $wide>
                    <Container $marginTop={20}>
                      {disabled && (
                        <Title level={1} $textAlign={'center'}>
                          {t('Access denied')}
                        </Title>
                      )}
                      <Form layout='vertical' form={form} name='form'>
                        {!disabled && (
                          <>
                            {filteredQuestionnaire &&
                            Array.from(filteredQuestionnaire).length > 0 ? (
                              filteredQuestionnaire
                                .slice(startSlicePos, startSlicePos + SHOWED_SECTIONS_COUNT)
                                .map((sectionObj) => (
                                  <div key={sectionObj.id}>
                                    <EntitySection
                                      entityType='questionnaire'
                                      questionnaireId={questionnaire?.id}
                                      questionnaireStatus={questionnaire?.status}
                                      section={sectionObj}
                                      control={control}
                                      watch={watch}
                                      setValue={setValue}
                                      submitCount={submitCount}
                                      trigger={trigger}
                                      adminView={adminView}
                                      referralView={referralView}
                                      disableAll={
                                        adminView || STATUSES_WITH_DISABLED[questionnaire?.status]
                                      }
                                      rejectedQuestions={rejectedQuestions}
                                      preFilledQuestions={preFilledQuestions}
                                      creators={creators}
                                      isSelectScoringTooltip={questionnaire?.isSelectScoringTooltip}
                                      isVeriffQuestionnaire={isVeriffQuestionnaire}
                                      {...(isVeriffQuestionnaire
                                        ? { sumSubStatusRes: sumSubStatusRes }
                                        : {})}
                                      {...(isVeriffQuestionnaire
                                        ? { refetchSumSubRes: refetchSumSubRes }
                                        : {})}
                                      isReadTooltip={isReadTooltip}
                                      {...props}
                                    />
                                  </div>
                                ))
                            ) : (
                              <Flex $marginTop={60} $justify='center'>
                                <Body2Light color={COLORS.greyMedium}>
                                  {t(
                                    'Something went wrong. Please, reload the page or try again later.'
                                  )}
                                </Body2Light>
                              </Flex>
                            )}
                          </>
                        )}
                      </Form>
                    </Container>

                    {verificationPhotos && verificationPhotos.data?.images?.length > 0 && (
                      <Container $wide $maxWidth={600} $marginTop={20}>
                        <Typography.Text>Veriff {t('Documents').toLowerCase()}</Typography.Text>
                        <Flex $marginTop={15} gap={3} vertical>
                          {verificationPhotos.data.images.map((image) => (
                            <ExternalLink
                              block={false}
                              key={image.id}
                              onClick={() => handleVerificationPhoto(image.id, image.name)}
                              style={{ justifyContent: 'flex-start' }}
                            >
                              {image.name}
                            </ExternalLink>
                          ))}
                        </Flex>
                      </Container>
                    )}
                  </Container>
                </Flex>
              </>
            )}
          </Flex>

          {!disabled &&
            filteredQuestionnaire &&
            filteredQuestionnaire.length > 0 &&
            Array.from(filteredQuestionnaire).length > 0 && (
              <Flex
                $marginTop={20}
                gap={20}
                justify='flex-end'
                wrap
                align='center'
                style={{
                  padding: '15px 105px 15px 16px',
                  background: '#f6f9ff',
                  boxShadow: '0px -3px 10px 0px rgba(0, 0, 0, 0.1)',
                  position: 'sticky',
                  bottom: 0
                }}
              >
                {scoring && (
                  <Scoring
                    isShowTotalPoints={
                      questionnaire?.isSelectTotalPoints || adminView || adminEditView
                    }
                    isShowCategoryScoring={
                      questionnaire?.isSelectCategoryScoring || adminView || adminEditView
                    }
                    scoring={scoring}
                  />
                )}
                {filteredQuestionnaire?.[0]?.visibleTypeGroup !== 'URL' && (
                  <>
                    {!isReadTooltip && (
                      <Body4 $color={COLORS.red}>
                        {t('Please review the document requirements to proceed')}
                      </Body4>
                    )}
                    {questionnaire?.status === 'UNACCEPTED' && (
                      <Container $minWidth={160}>
                        <Button
                          type='filled'
                          size='large'
                          text={t('Send on review')}
                          onClick={adminView ? onFinish : () => handleSubmit(onFinish)()}
                        />
                      </Container>
                    )}
                    <Container $minWidth={120}>
                      <Button
                        type='filled'
                        size='large'
                        text={t('Next')}
                        onClick={adminView ? onNext : () => handleSubmit(onNext)()}
                        disabled={disabled || disabledNext || !isReadTooltip}
                      />
                    </Container>
                  </>
                )}
              </Flex>
            )}
        </Col>
      </StyledRow>
    </>
  );
};

const HeaderRow = styled(Flex)`
  background: #f6f9ff;
  box-shadow: 0px 4px 14px 0px rgba(0, 0, 0, 0.1);
  padding: 20px 44px 20px 48px;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    padding: 12px 24px;
  }
`;

const CustomDivider = styled(Divider)`
  margin-left: 30px;
  @media (max-width: ${BREAKPOINTS.md}px) {
    display: none;
  }
`;

const CustomFloatButton = styled(FloatButton)`
  display: none;
  @media (max-width: ${BREAKPOINTS.md}px) {
    display: block;
  }
`;

const StyledRow = styled(Flex)`
  padding: 50px 0 0;
  min-height: calc(100dvh - 72px);

  @media (max-width: ${BREAKPOINTS.md}px) {
    gap: unset;
    padding: 40px 0 0;
  }
`;
